/**
 * evolve(state, event, meta) → state. Never validates and never fails: an appended event is a fact (§7.3).
 * Every record an event touches gets lastSeq/updatedBy/updatedAt from the meta. Defaults were written into the
 * event by `decide`, so nothing here invents values.
 *
 * Decision 0036: events upcast from v1 keep their stored `v: 1`; v2-only bookkeeping (an item's specRev, startedAt and
 * closedAt of tasks and intake, overdueNoticed) moves only on `v >= 2` events, so every hydration path agrees.
 */
import { legacyInitiativeState, legacyShapeFacts } from "@minga/protocol"
import { memoryRef, numberMemory } from "./knowledge/memory-refs"
import { numberOutputs, refOfPost } from "./knowledge/output-refs"
import { CLOSED, principalOf } from "./state"
import { senderAgentOf, uniqueParties } from "./threads"
import type {
  EntityRef,
  EventMeta,
  Ids,
  Item,
  ProjectEvent,
  ProjectState,
  Proposal,
  Review,
  Run,
} from "./types"

type Touchable = { readonly lastSeq: number; readonly updatedBy: unknown; readonly updatedAt: number }

const touch = <T extends Touchable>(r: T, m: EventMeta): T => ({
  ...r,
  lastSeq: m.seq,
  updatedBy: m.actor,
  updatedAt: m.ts,
})
/** A record written whole by this event: lastSeq, and contentSeq on records that carry one (ContentMeta). */
const insert = <T extends Touchable>(r: T, m: EventMeta): T =>
  "contentSeq" in r ? { ...r, lastSeq: m.seq, contentSeq: m.seq } : { ...r, lastSeq: m.seq }

const mapWhere = <T>(xs: ReadonlyArray<T>, pred: (x: T) => boolean, f: (x: T) => T): ReadonlyArray<T> =>
  xs.map((x) => (pred(x) ? f(x) : x))

/** Shallow merge of the keys present in a patch (exact optional keys: absent = unchanged). */
const merge = <T extends object>(r: T, patch: object): T => {
  const out: Record<string, unknown> = { ...(r as Record<string, unknown>) }
  for (const [k, v] of Object.entries(patch)) if (v !== undefined) out[k] = v
  return out as T
}

const SINGLE = /^([GKITPRWVCOMD])-(\d+)$/
const KEYED = /^[A-Z][A-Z0-9]{1,4}-(\d+)$/

/**
 * Raise the ref counter a minted ref used. Single letters raise their own counter (`C`, old intake, raises nothing);
 * a `<project key>-n` item ref raises `items`.
 */
const raiseRef = (s: ProjectState, ref: string): ProjectState["refCounters"] => {
  const m = SINGLE.exec(ref)
  if (m) {
    const key = m[1] as keyof ProjectState["refCounters"] | "C"
    if (key === "C") return s.refCounters
    const n = Number(m[2])
    return n > (s.refCounters[key] ?? 0) ? { ...s.refCounters, [key]: n } : s.refCounters
  }
  const k = KEYED.exec(ref)
  if (k) {
    const n = Number(k[1])
    return n > (s.refCounters.items ?? 0) ? { ...s.refCounters, items: n } : s.refCounters
  }
  return s.refCounters
}

/** Decision 0144: a decision's D-n raises the D counter. */
const decided = (ref: string | undefined, s: ProjectState): ProjectState =>
  ref === undefined ? s : { ...s, refCounters: raiseRef(s, ref) }

const updItem = (s: ProjectState, id: Ids["ItemId"], m: EventMeta, f: (t: Item) => Item): ProjectState => ({
  ...s,
  items: mapWhere(
    s.items,
    (t) => t.id === id,
    (t) => touch(f(t), m),
  ),
})
/**
 * A person acted on the item (decision 0040): the unattended attempt count starts over, and so does Wave A's count of
 * attempts of any kind (`autoAttempts`). Agents and the system never reset them.
 */
const personActed = (t: Item, m: EventMeta): Partial<Item> =>
  m.actor._tag !== "user"
    ? {}
    : {
        ...(t.unattendedAttempts !== undefined ? { unattendedAttempts: 0 } : {}),
        ...(t.autoAttempts !== undefined ? { autoAttempts: 0 } : {}),
      }

const updArtifact = (
  s: ProjectState,
  id: Ids["ArtifactId"],
  m: EventMeta,
  f: (a: ProjectState["artifacts"][number]) => ProjectState["artifacts"][number],
): ProjectState => ({
  ...s,
  artifacts: mapWhere(
    s.artifacts,
    (a) => a.id === id,
    (a) => touch(f(a), m),
  ),
})

const updRun = (s: ProjectState, id: Ids["RunId"], f: (r: Run) => Run): ProjectState => ({
  ...s,
  runs: mapWhere(s.runs, (r) => r.id === id, f),
})
const updProposal = (
  s: ProjectState,
  id: Ids["ProposalId"],
  m: EventMeta,
  f: (p: Proposal) => Proposal,
): ProjectState => ({
  ...s,
  proposals: mapWhere(
    s.proposals,
    (p) => p.id === id,
    (p) => touch(f(p), m),
  ),
})

const resolution = (
  m: EventMeta,
  extra: {
    optionKey?: string | null
    text?: string | null
    note?: string | null
    changesetId?: Ids["ChangesetId"] | null
    by?: EventMeta["actor"] | undefined
    /** Wave E (decision 0063): the card's recipient when someone else decided it. */
    for?: Ids["UserId"] | undefined
    /** Decision 0116: the agent that applied the person's chat answer. */
    relayedBy?: NonNullable<Proposal["resolution"]>["relayedBy"]
  },
): NonNullable<Proposal["resolution"]> => ({
  by: extra.by ?? m.actor,
  at: m.ts,
  optionKey: extra.optionKey ?? null,
  text: extra.text ?? null,
  note: extra.note ?? null,
  changesetId: extra.changesetId ?? null,
  ...(extra.for !== undefined ? { for: extra.for } : {}),
  ...(extra.relayedBy !== undefined ? { relayedBy: extra.relayedBy } : {}),
})

/** Review bookkeeping for ItemStatusChanged (the only place the round and stage move). */
const nextReview = (t: Item, e: Extract<ProjectEvent, { _tag: "ItemStatusChanged" }>): Review => {
  const r = t.review
  switch (e.cause._tag) {
    case "attempt_started":
    case "completed":
      return { ...r, stage: 0 }
    case "attempt_ended":
      return e.to === "in_review" ? { ...r, round: r.round + 1, stage: 0 } : r
    case "closed": {
      // a close opens a new round, so a human review question's intent key never collides (decision 0032); decision
      // 0101: a person's close that skipped the automated stages waits at the human stage
      const human = e.to === "in_review" && e.cause.skipped?.length ? r.stages.indexOf("human") : -1
      return { ...r, round: r.round + 1, stage: human >= 0 ? human : 0 }
    }
    case "merged": {
      // decision 0059: a run's merge moves an item in review to its human stage (the skipped stages got no verdict)
      if (e.to !== "in_review") return r
      const i = r.stages.indexOf("human", r.stage)
      return i >= 0 ? { ...r, stage: i } : r
    }
    case "review": {
      // decision 0050: a round a person sent back at the human stage is counted apart (not toward maxRounds)
      if (e.to === "todo" && e.cause.stage === "human")
        return { ...r, stage: 0, personRounds: (r.personRounds ?? 0) + 1 }
      if (e.to !== "in_review") return { ...r, stage: 0 }
      if (e.cause.verdict === "inconclusive") {
        // an inconclusive validator adds a human stage for this round (§7.4)
        const rest = r.stages.slice(r.stage + 1)
        if (rest.includes("human")) return { ...r, stage: r.stage + 1 + rest.indexOf("human") }
        const stages = [
          ...r.stages.slice(0, r.stage + 1),
          "human" as const,
          ...rest,
        ] as unknown as Review["stages"]
        return { ...r, stages, stage: r.stage + 1 }
      }
      return { ...r, stage: r.stage + 1 }
    }
    default:
      return r
  }
}

/** A legacy initiative item's status, pause and resolution recomputed from its v1 status and horizon (D9). */
const withLegacyState = (t: Item, ini: NonNullable<NonNullable<Item["legacy"]>["initiative"]>): Item => {
  const st = legacyInitiativeState(ini.status, ini.horizon)
  return {
    ...t,
    status: st.status,
    paused: st.paused,
    pausedBy: null,
    pauseNote: null,
    resolution: st.resolution,
    legacy: { ...t.legacy, initiative: ini },
  }
}

/** Wave C (decision 0071): one more timed turn for the agent on `day` (a different day counts from 0). */
const countTimedTurn = (s: ProjectState, agentId: string, day: string): ProjectState => {
  const cur = s.timedTurns?.[agentId]
  const next = cur && cur.day === day ? { ...cur, count: cur.count + 1 } : { day, count: 1, extra: false }
  return { ...s, timedTurns: { ...(s.timedTurns ?? {}), [agentId]: next } }
}

/**
 * Wave C: Continue on the day's cap card (`timed:<agentId>:<day>`) lifts the cap for the rest of that day; the held
 * wakes it delivers do not count.
 */
const liftTimedCap = (
  s: ProjectState,
  proposalId: Ids["ProposalId"],
  optionKey: string | null,
): ProjectState => {
  if (optionKey !== "continue") return s
  const p = s.proposals.find((x) => x.id === proposalId)
  const k = p ? /^timed:([^:]+):(\d{4}-\d{2}-\d{2})$/.exec(p.intentKey) : null
  if (!k) return s
  const agentId = k[1] as string
  const day = k[2] as string
  const cur = s.timedTurns?.[agentId]
  const next = cur && cur.day === day ? { ...cur, extra: true } : { day, count: 0, extra: true }
  return { ...s, timedTurns: { ...(s.timedTurns ?? {}), [agentId]: next } }
}

export const evolve = (s0: ProjectState, e: ProjectEvent, m: EventMeta): ProjectState => {
  const s: ProjectState = { ...s0, seq: m.seq }
  const v2 = m.v >= 2
  switch (e._tag) {
    // ---- genesis, settings, sync ----
    case "ProjectCreated":
      return { ...s, project: insert(e.project, m) }
    case "ProjectUpdated":
      return s.project ? { ...s, project: touch(merge(s.project, e.patch), m) } : s
    case "BriefRevised":
      return s.project ? { ...s, project: touch({ ...s.project, description: e.body }, m) } : s
    case "WorkspaceSynced":
      return { ...s, members: e.members, mayor: e.mayor }
    case "PolicyCreated":
      return { ...s, policies: [...s.policies, insert(e.policy, m)] }
    case "PolicyRevised":
      return {
        ...s,
        policies: mapWhere(
          s.policies,
          (p) => p.id === e.policyId,
          (p) => touch({ ...p, revision: e.revision, document: e.document, restoredFrom: e.restoredFrom }, m),
        ),
      }
    // ---- areas and agents ----
    case "AreaCreated":
      return { ...s, areas: [...s.areas, insert(e.area, m)] }
    case "AreaUpdated":
      return {
        ...s,
        areas: mapWhere(
          s.areas,
          (a) => a.id === e.areaId,
          (a) => touch(merge(a, e.patch), m),
        ),
      }
    case "AreaCharterRevised":
      return {
        ...s,
        areas: mapWhere(
          s.areas,
          (a) => a.id === e.areaId,
          (a) => touch({ ...a, charter: e.charter }, m),
        ),
      }
    case "AreaArchived":
      return {
        ...s,
        areas: mapWhere(
          s.areas,
          (a) => a.id === e.areaId,
          (a) => touch({ ...a, status: "archived" as const }, m),
        ),
      }
    case "IntegrationGranted":
      return {
        ...s,
        areas: mapWhere(
          s.areas,
          (a) => a.id === e.areaId,
          (a) =>
            touch(
              {
                ...a,
                grants: [...a.grants.filter((g) => g.integrationId !== e.grant.integrationId), e.grant],
              },
              m,
            ),
        ),
      }
    case "IntegrationRevoked":
      return {
        ...s,
        areas: mapWhere(
          s.areas,
          (a) => a.id === e.areaId,
          (a) => touch({ ...a, grants: a.grants.filter((g) => g.integrationId !== e.integrationId) }, m),
        ),
      }
    case "AgentHired":
      return { ...s, agents: [...s.agents, insert(e.agent, m)] }
    case "AgentUpdated":
      return {
        ...s,
        agents: mapWhere(
          s.agents,
          (a) => a.id === e.agentId,
          (a) => touch(merge(a, e.patch), m),
        ),
      }
    case "AgentCharterRevised":
      return {
        ...s,
        agents: mapWhere(
          s.agents,
          (a) => a.id === e.agentId,
          // wave C (decision 0071): who last revised it and when (About's edit line, Changed for you, the turn file)
          (a) => touch({ ...a, charter: e.charter, charterEdit: { by: m.actor, at: m.ts } }, m),
        ),
      }
    case "AgentMindChanged":
      return {
        ...s,
        agents: mapWhere(
          s.agents,
          (a) => a.id === e.agentId,
          (a) => touch({ ...a, mind: e.mind }, m),
        ),
      }
    case "AgentStatusChanged":
      return {
        ...s,
        agents: mapWhere(
          s.agents,
          (a) => a.id === e.agentId,
          (a) => touch({ ...a, status: e.status }, m),
        ),
      }
    // ---- goals and KPIs ----
    case "GoalCreated":
      return { ...s, goals: [...s.goals, insert(e.goal, m)], refCounters: raiseRef(s, e.goal.ref) }
    case "GoalUpdated":
      return {
        ...s,
        goals: mapWhere(
          s.goals,
          (g) => g.id === e.goalId,
          (g) => touch(merge(g, e.patch), m),
        ),
      }
    case "GoalStatusChanged":
      return {
        ...s,
        goals: mapWhere(
          s.goals,
          (g) => g.id === e.goalId,
          (g) => touch({ ...g, status: e.status }, m),
        ),
      }
    case "TargetChanged": {
      const subj = e.subject
      if (subj._tag === "goal")
        return {
          ...s,
          goals: mapWhere(
            s.goals,
            (g) => g.id === subj.id,
            (g) => touch({ ...g, target: e.condition }, m),
          ),
        }
      return updItem(s, subj.id, m, (i) =>
        i.bet ? { ...i, bet: { ...i.bet, success: { ...i.bet.success, condition: e.condition } } } : i,
      )
    }
    case "KpiDefined":
      return { ...s, kpis: [...s.kpis, insert(e.kpi, m)], refCounters: raiseRef(s, e.kpi.ref) }
    case "KpiUpdated":
      return {
        ...s,
        kpis: mapWhere(
          s.kpis,
          (k) => k.id === e.kpiId,
          (k) => touch(merge(k, e.patch), m),
        ),
      }
    // decision 0098: the agent's newest brief (not the project brief: that is BriefRevised)
    case "BriefSent": {
      // decision 0144: a sectioned brief keeps its sections next to its markdown
      const sections = e.sections !== undefined ? { sections: e.sections } : {}
      return {
        ...s,
        briefs: { ...(s.briefs ?? {}), [e.agentId]: { to: e.to, body: e.body, at: e.at, ...sections } },
        // decision 0130: the newest per person
        briefsTo: {
          ...(s.briefsTo ?? {}),
          [e.agentId]: {
            ...(s.briefsTo?.[e.agentId] ?? {}),
            [e.to]: { body: e.body, at: e.at, ...sections },
          },
        },
      }
    }
    case "KpiSampleRecorded":
      return {
        ...s,
        kpis: mapWhere(
          s.kpis,
          (k) => k.id === e.kpiId,
          (k) =>
            touch(
              k.latest && k.latest.at > e.sample.at
                ? k
                : {
                    ...k,
                    latest: { value: e.sample.value, at: e.sample.at, provisional: e.sample.provisional },
                  },
              m,
            ),
        ),
      }
    // ---- items ----
    case "ItemCreated":
      return { ...s, items: [...s.items, insert(e.item, m)], refCounters: raiseRef(s, e.item.ref) }
    case "ItemUpdated":
      return updItem(s, e.itemId, m, (t) => {
        const next = merge(t, e.patch)
        // a description change is a new revision ("rev 3 → 4"); v1 events never bump it
        return v2 && e.patch.body !== undefined && e.patch.body !== e.before.body
          ? { ...next, specRev: { n: (t.specRev.n + 1) as Item["specRev"]["n"], at: m.ts, by: m.actor } }
          : next
      })
    case "ItemMoved":
      return updItem(s, e.itemId, m, (t) => ({ ...t, parentId: e.parentId, goalIds: e.goalIds }))
    case "ItemAssigned":
      return updItem(s, e.itemId, m, (t) => ({
        ...t,
        owner: e.owner,
        ...(e.assignee !== undefined ? { assignee: e.assignee } : {}),
      }))
    case "ChecksRevised":
      return updItem(s, e.itemId, m, (t) => ({
        ...t,
        checks: e.checks,
        // decision 0116: who wrote them (events from before 0116 don't say: unchanged)
        ...(e.by !== undefined ? { checksBy: e.by } : {}),
        review: e.review,
        ...personActed(t, m),
      }))
    case "ItemStatusChanged":
      return updItem(s, e.itemId, m, (t) => {
        const c = e.cause
        const base: Item = {
          ...t,
          ...personActed(t, m),
          status: e.to,
          blocked: e.to === "blocked" ? e.blocked : null,
          resolution: e.resolution,
          review: nextReview(t, e),
          ...(v2
            ? {
                startedAt: e.to === "in_progress" && t.startedAt === null ? m.ts : t.startedAt,
                closedAt: CLOSED.has(e.to) ? m.ts : CLOSED.has(e.from) ? null : t.closedAt,
              }
            : {}),
        }
        switch (c._tag) {
          case "attempt_started": {
            const { closeEvidence: _cleared, result: _result, ...rest } = base
            return {
              ...rest,
              activeRunId: c.runId,
              attempts: t.attempts + 1,
              // v2 bookkeeping (decision 0036): v1 events never write it, so a v1 fold equals the v1 snapshot
              ...(v2
                ? {
                    unattendedAttempts: (base.unattendedAttempts ?? 0) + 1,
                    // Wave A §6: every attempt since a person last acted, progressed or not (the ceiling)
                    autoAttempts: (base.autoAttempts ?? 0) + 1,
                  }
                : {}),
              retry: null,
            }
          }
          case "closed": {
            // decision 0101: the close's result replaces any earlier one
            const { result: _old, ...rest } = base
            return { ...rest, closeEvidence: c.evidence, ...(c.result ? { result: c.result } : {}) }
          }
          case "attempt_ended":
            return {
              ...base,
              activeRunId: null,
              lastRunId: c.runId,
              retry: c.retry,
              // Wave A §6: an attempt that made progress is not counted: its attempt_started's +1 is taken back (the
              // count is not reset, so attempts that changed nothing still add up until a person acts)
              ...(v2 && c.progressed
                ? { unattendedAttempts: Math.max(0, (base.unattendedAttempts ?? 0) - 1) }
                : {}),
            }
          case "retry":
            return { ...base, retry: null }
          default:
            return base
        }
      })
    case "ItemPaused":
      // decision 0032: who paused it. Events from before that wave carry no `by`: derive it from the event's actor (a
      // person's pause had a user actor; a stop's pause came from the runner or the scheduler → null).
      return updItem(s, e.itemId, m, (t) =>
        e.paused
          ? {
              ...t,
              paused: true,
              pausedBy: e.by !== undefined ? e.by : principalOf(m.actor),
              pauseNote: e.note ?? null,
            }
          : { ...t, paused: false, pausedBy: null, pauseNote: null, ...personActed(t, m) },
      )
    case "ItemOverdue":
      return updItem(s, e.itemId, m, (t) => ({ ...t, overdueNoticed: e.dueAt }))
    case "LegacyInitiativeUpdated":
      return updItem(s, e.itemId, m, (t) => {
        const p = e.patch
        let next: Item = { ...t }
        if (p.title !== undefined) next = { ...next, title: p.title }
        if (p.why !== undefined) next = { ...next, why: p.why }
        if (p.body !== undefined) next = { ...next, body: p.body }
        if (p.tags !== undefined) next = { ...next, tags: p.tags }
        if (p.goalIds !== undefined) next = { ...next, goalIds: [...p.goalIds] }
        if (p.ownerAgentId !== undefined)
          next = {
            ...next,
            owner: p.ownerAgentId === null ? null : { _tag: "agent", agentId: p.ownerAgentId },
          }
        const ini = t.legacy?.initiative
        if (!ini) return next
        let li = ini
        if (p.shape !== undefined) {
          const f = legacyShapeFacts(p.shape)
          next = { ...next, type: f.type, bet: f.bet, dueAt: f.dueAt }
          li = { ...li, definitionOfDone: f.definitionOfDone, supportsKpiId: f.supportsKpiId }
        }
        if (p.horizon !== undefined) li = { ...li, horizon: p.horizon }
        return withLegacyState(next, li)
      })
    case "LegacyInitiativeStatusChanged":
      return updItem(s, e.itemId, m, (t) => {
        const ini = t.legacy?.initiative
        if (!ini) return t
        const next = withLegacyState(t, { ...ini, status: e.status })
        return {
          ...next,
          startedAt: e.status === "active" && t.startedAt === null ? m.ts : t.startedAt,
          closedAt:
            e.status === "done" || e.status === "killed" || e.status === "abandoned" ? m.ts : t.closedAt,
        }
      })
    case "BetVerdictRecorded":
      return updItem(s, e.itemId, m, (i) =>
        i.bet
          ? { ...i, bet: { ...i.bet, verdict: { value: e.value, by: e.by, at: m.ts, evidence: e.evidence } } }
          : i,
      )
    case "RequestDecided":
      return updItem(s, e.itemId, m, (t) => ({
        ...t,
        request: t.request
          ? { ...t.request, state: e.accepted ? ("accepted" as const) : ("declined" as const) }
          : t.request,
      }))
    case "ReviewRecorded":
      return updItem(s, e.itemId, m, (t) => t)
    case "RankChanged": {
      const ref = e.ref
      if (ref._tag === "goal")
        return {
          ...s,
          goals: mapWhere(
            s.goals,
            (g) => g.id === ref.id,
            (g) => touch({ ...g, rank: e.rank }, m),
          ),
        }
      return updItem(s, ref.id, m, (t) => ({ ...t, rank: e.rank }))
    }
    // ---- edges, holds, plan versions ----
    case "EdgeAdded":
      return { ...s, edges: [...s.edges, insert(e.edge, m)] }
    case "EdgeRemoved":
      return { ...s, edges: s.edges.filter((x) => x.id !== e.edgeId) }
    case "HoldSet": {
      const ref = e.ref
      switch (ref._tag) {
        case "goal":
          return {
            ...s,
            goals: mapWhere(
              s.goals,
              (g) => g.id === ref.id,
              (g) => touch({ ...g, hold: e.hold }, m),
            ),
          }
        case "item":
          return updItem(s, ref.id, m, (t) => ({ ...t, hold: e.hold }))
        case "edge":
          return {
            ...s,
            edges: mapWhere(
              s.edges,
              (x) => x.id === ref.id,
              (x) => touch({ ...x, hold: e.hold }, m),
            ),
          }
        default:
          return s
      }
    }
    case "ChangesetApplied":
      return { ...s, planVersion: e.planVersion }
    // ---- proposal ledger ----
    case "ProposalOpened":
      return {
        ...s,
        proposals: [...s.proposals, insert(e.proposal, m)],
        refCounters: raiseRef(s, e.proposal.ref),
      }
    case "ProposalRevised":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        contentSeq: m.seq,
        revision: e.revision,
        contentHash: e.contentHash,
        body: e.body,
        reasoning: e.reasoning,
        ...(e.asked !== undefined ? { asked: e.asked } : {}),
        state: "open",
        perChange: e.body._tag === "plan_change" ? e.body.changes.map(() => "pending" as const) : [],
      }))
    case "ProposalRevisionRequested":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "revision_requested",
        resolution: resolution(m, { note: e.note }),
      }))
    case "ProposalApproved":
      return decided(
        e.decisionRef,
        updProposal(s, e.proposalId, m, (p) => ({
          ...p,
          ...(e.decisionRef !== undefined ? { decisionRef: e.decisionRef } : {}),
          state: "approved",
          perChange:
            e.selection === null
              ? p.perChange.map(() => "accepted" as const)
              : p.perChange.map((_, i) =>
                  (e.selection ?? []).includes(i) ? ("accepted" as const) : ("rejected" as const),
                ),
          resolution: resolution(m, {
            ...(e.note !== undefined ? { note: e.note } : {}),
            ...(e.for !== undefined ? { for: e.for } : {}),
            ...(e.relayedBy !== undefined ? { relayedBy: e.relayedBy } : {}),
          }),
        })),
      )
    case "ProposalApplied":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "applied",
        resolution: { ...(p.resolution ?? resolution(m, {})), changesetId: e.changesetId },
      }))
    case "ProposalApplyFailed":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "apply_failed",
        // decision 0139: the reason, not only its kind, so the card says why it did not apply
        resolution: {
          ...(p.resolution ?? resolution(m, {})),
          note: `apply failed: ${
            e.rejection._tag === "Invalid"
              ? (e.rejection.issues[0]?.message ?? e.rejection._tag)
              : e.rejection._tag === "Forbidden"
                ? e.rejection.detail
                : e.rejection._tag
          }`,
        },
      }))
    case "ProposalRejected":
      return decided(
        e.decisionRef,
        updProposal(s, e.proposalId, m, (p) => ({
          ...p,
          ...(e.decisionRef !== undefined ? { decisionRef: e.decisionRef } : {}),
          state: "rejected",
          perChange: p.perChange.map(() => "rejected" as const),
          resolution: resolution(m, {
            note: e.reason,
            ...(e.for !== undefined ? { for: e.for } : {}),
            ...(e.relayedBy !== undefined ? { relayedBy: e.relayedBy } : {}),
          }),
        })),
      )
    case "ProposalAnswered":
      return decided(
        e.decisionRef,
        updProposal(liftTimedCap(s, e.proposalId, e.optionKey), e.proposalId, m, (p) => ({
          ...p,
          ...(e.decisionRef !== undefined ? { decisionRef: e.decisionRef } : {}),
          state: "answered",
          // decision 0059: a merge reported by a runner names who decided (`by`) and how (`note`)
          resolution: resolution(m, {
            optionKey: e.optionKey,
            text: e.text,
            note: e.note ?? null,
            by: e.by,
            ...(e.for !== undefined ? { for: e.for } : {}),
            ...(e.relayedBy !== undefined ? { relayedBy: e.relayedBy } : {}),
          }),
        })),
      )
    case "ProposalExpired":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "expired",
        resolution: resolution(m, { optionKey: e.appliedDefault }),
      }))
    case "ProposalWithdrawn":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "withdrawn",
        resolution: resolution(m, {}),
      }))
    // decision 0102: one step up the chain; the card stays open for its new addressee
    case "ProposalEscalated":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        approver: e.to,
        escalations: [...(p.escalations ?? []), { by: e.by, at: m.ts, to: e.to, note: e.note }].slice(-10),
      }))
    case "ProposalAdopted":
      return updProposal(s, e.proposalId, m, (p) => ({
        ...p,
        state: "adopted",
        resolution: resolution(m, { changesetId: e.changesetId }),
      }))
    // ---- comments, the agent bus, artifacts ----
    case "CommentPosted": {
      // decision 0144: a decision comment's D-n raises the counter (the comment itself lives in the projection)
      const n = e.comment.ref !== undefined ? { ...s, refCounters: raiseRef(s, e.comment.ref) } : s
      const asked =
        e.comment.kind === "ask" && e.comment.ask?.state === "open"
          ? { ...n, openAsks: [...n.openAsks, insert(e.comment, m)] }
          : n
      // decision 0142: a message in a thread moves the thread on
      const threadId = e.comment.threadId
      if (!s.threads?.some((t) => t.id === threadId)) return asked
      return {
        ...asked,
        threads: s.threads.map((t) =>
          t.id === threadId ? { ...t, lastAt: m.ts, lastSeq: m.seq, messages: t.messages + 1 } : t,
        ),
      }
    }
    case "ThreadOpened": {
      const n = Number(/^T-(\d+)$/.exec(e.thread.ref)?.[1] ?? 0)
      return {
        ...s,
        threads: [...(s.threads ?? []), { ...e.thread, lastSeq: m.seq }],
        refCounters: n > (s.refCounters.threads ?? 0) ? { ...s.refCounters, threads: n } : s.refCounters,
      }
    }
    case "MessageDelivered": {
      const threadId = e.threadId
      if (threadId === null || !s.threads?.some((t) => t.id === threadId)) return s
      const to = e.to
      const commentId = e.commentId
      return {
        ...s,
        threads: s.threads.map((t) => {
          if (t.id !== threadId) return t
          const parties = uniqueParties([...t.parties, ...(e.from ? [e.from] : []), to])
          if (to._tag !== "external" || commentId === null) return { ...t, parties }
          const others = t.pending.filter(
            (p) => !(p.commentId === commentId && p.participantId === to.participantId),
          )
          if (e.state === "read") return { ...t, parties, pending: others }
          const sender = senderAgentOf(m.actor)
          return {
            ...t,
            parties,
            pending: [
              ...others,
              {
                commentId,
                participantId: to.participantId,
                ownerId: to.ownerId,
                kind: to.kind,
                ...(to.clientId !== undefined ? { clientId: to.clientId } : {}),
                at: m.ts,
                ...(sender ? { sender } : {}),
              },
            ],
          }
        }),
      }
    }
    case "CommentEdited":
      return {
        ...s,
        openAsks: mapWhere(
          s.openAsks,
          (c) => c.id === e.commentId,
          (c) => touch({ ...c, body: e.body, editedAt: m.ts, contentSeq: m.seq }, m),
        ),
      }
    case "ThreadResolved":
      return {
        ...s,
        openAsks: mapWhere(
          s.openAsks,
          (c) => c.threadId === e.threadId,
          (c) => touch({ ...c, resolvedAt: m.ts }, m),
        ),
      }
    case "AskStateChanged":
      return { ...s, openAsks: s.openAsks.filter((c) => c.id !== e.askId) }
    case "ArtifactPosted": {
      // wave K: decide mints O-n; an event from before the wave gets its chain's ref or the next number here (post
      // order). A state from before the wave (a stored snapshot) is numbered first, so every path numbers the same
      // (`numberOutputs`).
      const n = numberOutputs(s)
      const ref = e.artifact.ref ?? refOfPost(n, e.artifact.supersedes)
      const artifact = e.artifact.ref === undefined ? { ...e.artifact, ref } : e.artifact
      return { ...n, artifacts: [...n.artifacts, insert(artifact, m)], refCounters: raiseRef(n, ref) }
    }
    case "ArtifactStateChanged":
      return {
        ...s,
        artifacts: mapWhere(
          s.artifacts,
          (a) => a.id === e.artifactId,
          (a) => touch({ ...a, state: e.state }, m),
        ),
      }
    // ---- the PR watch and Merge (decision 0059) ----
    case "PrStateObserved":
      return updArtifact(s, e.artifactId, m, (a) => {
        const merged = e.facts.find((f) => f._tag === "merged")
        const via = merged?._tag === "merged" ? merged.via : a.pr?.mergedVia
        return {
          ...a,
          pr: {
            snapshot: e.snapshot,
            hash: e.hash,
            told: [...(a.pr?.told ?? []), ...e.facts.map((f) => f.key)].slice(-50),
            ...(via !== undefined ? { mergedVia: via } : {}),
            ...(a.pr?.reviewed !== undefined ? { reviewed: a.pr.reviewed } : {}),
          },
        }
      })
    case "PrReviewBound":
      return updArtifact(s, e.artifactId, m, (a) =>
        a.pr
          ? { ...a, pr: { ...a.pr, reviewed: { proposalId: e.proposalId, sha: e.sha, source: e.source } } }
          : a,
      )
    case "MergeRequested":
      return updArtifact(s, e.artifactId, m, (a) => ({
        ...a,
        merge: {
          mergeId: e.mergeId,
          proposalId: e.proposalId,
          headSha: e.headSha,
          by: e.by,
          at: m.ts,
          notAfter: e.notAfter,
          state: "merging",
          sha: null,
          sentence: null,
          endedAt: null,
        },
      }))
    case "MergeFinished":
      return updArtifact(s, e.artifactId, m, (a) => {
        const r = e.result
        const state =
          r._tag === "merged"
            ? "merged"
            : r._tag === "queued"
              ? "queued"
              : r._tag === "refused"
                ? "refused"
                : "expired"
        const sha = r._tag === "merged" ? r.sha : null
        if (a.merge?.mergeId === e.mergeId)
          return {
            ...a,
            merge: {
              ...a.merge,
              state,
              sha: sha ?? a.merge.sha,
              sentence: e.sentence,
              endedAt: state === "queued" ? null : m.ts,
            },
          }
        // an immediate refusal (no MergeRequested): the card's binding names the proposal and the reviewed SHA
        const bound = a.pr?.reviewed
        if (!bound || e.by === undefined) return a
        return {
          ...a,
          merge: {
            mergeId: e.mergeId,
            proposalId: bound.proposalId,
            headSha: bound.sha,
            by: e.by,
            at: m.ts,
            notAfter: m.ts,
            state,
            sha,
            sentence: e.sentence,
            endedAt: m.ts,
          },
        }
      })
    // ---- runs ----
    case "RunRequested": {
      const run = e.branch === undefined ? e.run : { ...e.run, branch: e.branch }
      const counters = raiseRef(s, e.run.ref)
      // settings wave K11: W-n / V-n of a temporary teammate
      const refCounters = e.run.worker
        ? raiseRef({ ...s, refCounters: counters }, e.run.worker.ref)
        : counters
      // re-walk 0166 §2: the asker's own turn now carries what a queued message of theirs waited on
      const asker = run.kind === "turn" && run.askedBy?._tag === "user" ? run.askedBy.userId : null
      const askWaits =
        asker !== null && s.askWaits?.some((w) => w.agentId === run.agentId && w.askerId === asker)
          ? s.askWaits.filter((w) => !(w.agentId === run.agentId && w.askerId === asker))
          : s.askWaits
      return {
        ...s,
        runs: [...s.runs, run],
        refCounters,
        ...(askWaits !== s.askWaits ? { askWaits } : {}),
      }
    }
    case "AskWaiting": {
      // re-walk 0166 §2: one per agent and asker; no reasons = it would run (cleared)
      const rest = (s.askWaits ?? []).filter((w) => !(w.agentId === e.agentId && w.askerId === e.askerId))
      const next =
        e.unmet.length === 0
          ? rest
          : [
              ...rest,
              {
                agentId: e.agentId,
                askerId: e.askerId,
                unmet: e.unmet,
                since: e.since,
                ...(e.lendable !== undefined && e.lendable.length > 0 ? { lendable: e.lendable } : {}),
              },
            ]
      return { ...s, askWaits: next }
    }
    case "RunWaiting":
      return updRun(s, e.runId, (r) => ({
        ...r,
        state: {
          _tag: "waiting",
          unmet: e.unmet,
          since: e.since,
          // wave E (decision 0063): other people's runners that could take it if lent to the item
          ...(e.lendable !== undefined && e.lendable.length > 0 ? { lendable: e.lendable } : {}),
        },
      }))
    case "RunLeased":
      return updRun(s, e.runId, (r) => ({
        ...r,
        epoch: e.epoch,
        executor: e.executor,
        state: { _tag: "leased", epoch: e.epoch, phase: "preparing" },
        leasedAt: m.ts,
        phaseSince: m.ts,
      }))
    case "RunRequeued":
      return updRun(s, e.runId, (r) => ({
        ...r,
        prestartReleases: e.prestartReleases,
        state: { _tag: "requested" },
      }))
    case "RunStarted": {
      const run = s.runs.find((r) => r.id === e.runId)
      const s1 = updRun(s, e.runId, (r) => ({
        ...r,
        epoch: e.epoch,
        state: { _tag: "leased", epoch: e.epoch, phase: "running" },
        startedAt: m.ts,
        phaseSince: m.ts,
        sessionIn: e.sessionIn,
        taint: r.taint === "tainted" ? "tainted" : e.taint,
      }))
      // the first attempt freezes the checks (and touches the item, as v1 did for the task)
      if (run?.kind === "worker" && run.itemId)
        return updItem(s1, run.itemId, m, (t) =>
          t.checksFrozenAt === null ? { ...t, checksFrozenAt: m.ts } : t,
        )
      return s1
    }
    case "RunMilestone":
      return updRun(s, e.runId, (r0) => {
        const r = {
          ...r0,
          lastMilestone: { kind: e.kind, text: e.text, ref: e.ref, at: m.ts },
          // Wave A §6: the attempt pushed (`open_pull_request` posts `pr_opened` after a push); v2 events only, so a
          // v1 fold still equals the v1 snapshot (decision 0036)
          ...(m.v >= 2 && e.kind === "pr_opened" ? { pushedAt: r0.pushedAt ?? m.ts } : {}),
        }
        if (r.state._tag !== "leased") return r
        const phase = e.kind === "awaiting_permission" ? "awaiting_permission" : "running"
        return phase === r.state.phase ? r : { ...r, state: { ...r.state, phase }, phaseSince: m.ts }
      })
    case "RunOutcomeReported":
      return updRun(s, e.runId, (r) => ({ ...r, claimed: e.outcome }))
    case "RunVerdictReported":
      return updRun(s, e.runId, (r) => ({ ...r, verdict: e.verdict }))
    case "RunTaintRaised":
      return updRun(s, e.runId, (r) => ({ ...r, taint: "tainted" }))
    case "RunCancelRequested":
      // decision 0032: who asked to stop it (display, and the stop's ItemPaused.by). The first requester stays,
      // except that a person's request replaces an agent's: an agent's later stop never takes over a person's.
      return updRun(s, e.runId, (r0) => {
        // decision 0116: a re-placement stop re-dispatches only while nobody asked to stop the run for real
        const { cancelRedispatch: _, ...rest } = r0
        const r =
          e.redispatch === true && (r0.cancelRedispatch === true || r0.cancelRequestedBy === undefined)
            ? { ...rest, cancelRedispatch: true }
            : rest
        const prev = r.cancelRequestedBy ?? null
        const next = principalOf(m.actor)
        const timestamp =
          e.requestedAt !== undefined ? { cancelRequestedAt: r.cancelRequestedAt ?? e.requestedAt } : {}
        if (prev === null || (prev._tag !== "user" && next?._tag === "user"))
          return { ...r, cancelRequestedBy: next, ...timestamp }
        return { ...r, ...timestamp }
      })
    case "RunEnded":
      return updRun(s, e.runId, (r) => ({
        ...r,
        state: { _tag: "ended", stopReason: e.stopReason },
        stopReason: e.stopReason,
        claimed: e.claimed,
        outcome: e.outcome,
        downgrade: e.downgrade,
        verdict: e.verdict ?? r.verdict,
        usage: e.usage,
        error: e.error,
        sessionOut: e.sessionOut,
        endedAt: m.ts,
        ...(e.head !== undefined ? { head: e.head } : {}),
        // wave F (decision 0062)
        ...(e.fundedBy !== undefined ? { fundedBy: e.fundedBy } : {}),
        ...(e.compute !== undefined ? { compute: e.compute } : {}),
        // decision 0095
        ...(e.place !== undefined ? { place: e.place } : {}),
        // decision 0149
        ...(e.resources !== undefined ? { resources: e.resources } : {}),
      }))
    case "OutwardActionExecuted":
    case "OutwardActionFailed":
      return updProposal(s, e.proposalId, m, (p) => p)
    // ---- memory ----
    case "MemoryItemCreated": {
      // wave C (decision 0071): decide mints M-n; an event from before the wave gets the next number here, after a
      // snapshot from before the wave is numbered (`numberMemory`), so every path numbers the same
      const n = numberMemory(s)
      const ref = e.item.ref ?? memoryRef((n.refCounters.M ?? 0) + 1)
      const item = e.item.ref === undefined ? { ...e.item, ref } : e.item
      return {
        ...n,
        memory: [...n.memory.filter((x) => x.id !== e.item.id), insert(item, m)],
        refCounters: raiseRef(n, ref),
      }
    }
    case "MemoryItemRevised": {
      // a revision keeps the ref (an event from before the wave carries none: the item's own)
      const n = numberMemory(s)
      return {
        ...n,
        memory: mapWhere(
          n.memory,
          (x) => x.id === e.itemId,
          (x) =>
            insert(e.item.ref === undefined && x.ref !== undefined ? { ...e.item, ref: x.ref } : e.item, m),
        ),
      }
    }
    case "MemoryItemArchived":
      return {
        ...s,
        memory: mapWhere(
          s.memory,
          (x) => x.id === e.itemId,
          (x) => touch({ ...x, status: "archived" as const }, m),
        ),
      }
    case "MemoryItemRevoked":
      return {
        ...s,
        memory: mapWhere(
          s.memory,
          (x) => x.id === e.itemId,
          (x) => touch({ ...x, status: "revoked" as const }, m),
        ),
      }
    // ---- wave C (decision 0071): monitors and the clock (one clock, in the Project DO) ----
    case "MonitorSet":
      return {
        ...s,
        monitors: [...(s.monitors ?? []).filter((x) => x.id !== e.monitor.id), e.monitor],
      }
    case "ProjectWorkRequested":
    case "ProjectWorkReceived":
      return {
        ...s,
        workRequests: [...(s.workRequests ?? []).filter((r) => r.id !== e.request.id), e.request],
      }
    case "ProjectWorkLinked":
      return updItem(s, e.itemId, m, (item) => ({
        ...item,
        workRequest: { requestId: e.requestId, revision: e.revision },
      }))
    case "ProjectWorkCancelled":
    case "ProjectWorkStopped":
      return {
        ...s,
        workRequests: (s.workRequests ?? []).map((r) =>
          r.id === e.requestId
            ? {
                ...r,
                state: "cancelled" as const,
                updatedAt: m.ts,
                retired: [
                  ...(r.retired ?? []),
                  {
                    ...(e._tag === "ProjectWorkStopped" && e.assignment
                      ? e.assignment
                      : {
                          revision: e.revision,
                          target: r.target,
                          requester: e._tag === "ProjectWorkCancelled" ? e.requester : r.requester,
                          authorizedBy: m.actor,
                          note: e.note,
                          at: m.ts,
                        }),
                    delivery: {
                      _tag: e._tag === "ProjectWorkStopped" ? ("delivered" as const) : ("pending" as const),
                    },
                  },
                ],
              }
            : r,
        ),
      }
    case "ProjectWorkReplied":
    case "ProjectWorkReplyReceived":
      return {
        ...s,
        workRequests: (s.workRequests ?? []).map((r) =>
          r.id === e.requestId
            ? {
                ...r,
                replies: [...r.replies, e.reply],
                updatedAt: e.reply.at,
                state:
                  e.reply.revision === r.revision && r.state !== "cancelled"
                    ? e.reply.outcome === "completed" || e.reply.outcome === "declined"
                      ? ("answered" as const)
                      : ("pending" as const)
                    : r.state,
              }
            : r,
        ),
      }
    case "ProjectWorkVerified":
      return {
        ...s,
        workRequests: (s.workRequests ?? []).map((r) =>
          r.id === e.requestId
            ? {
                ...r,
                verifications: [...(r.verifications ?? []), e.verification],
                updatedAt: e.verification.at,
              }
            : r,
        ),
      }
    case "WorkDeliveryRecorded":
      return {
        ...s,
        workRequests: (s.workRequests ?? []).map((r) =>
          r.id === e.requestId && (e.control || e.replyId !== undefined || r.revision === e.revision)
            ? {
                ...r,
                ...(e.control
                  ? {
                      retired:
                        r.retired?.map((a) =>
                          a.revision === e.revision
                            ? {
                                ...a,
                                delivery:
                                  e.problem === null
                                    ? { _tag: "delivered" as const }
                                    : { _tag: "blocked" as const, reason: e.problem },
                              }
                            : a,
                        ) ?? [],
                    }
                  : e.replyId
                    ? {
                        replies: r.replies.map((reply) =>
                          reply.id === e.replyId
                            ? {
                                ...reply,
                                delivery:
                                  e.problem === null
                                    ? { _tag: "delivered" as const }
                                    : { _tag: "blocked" as const, reason: e.problem },
                              }
                            : reply,
                        ),
                      }
                    : {
                        delivery:
                          e.problem === null
                            ? { _tag: "delivered" as const }
                            : { _tag: "blocked" as const, reason: e.problem },
                      }),
              }
            : r,
        ),
      }
    case "MonitorRemoved":
      return { ...s, monitors: (s.monitors ?? []).filter((x) => x.id !== e.monitorId) }
    case "MonitorPaused":
      return {
        ...s,
        monitors: mapWhere(
          s.monitors ?? [],
          (x) => x.id === e.monitorId,
          (x) => ({ ...x, enabled: !e.paused, pausedBy: e.paused ? m.actor : null }),
        ),
      }
    case "MonitorFired": {
      const mon = (s.monitors ?? []).find((x) => x.id === e.monitorId)
      const s1: ProjectState = {
        ...s,
        monitors: mapWhere(
          s.monitors ?? [],
          (x) => x.id === e.monitorId,
          (x) => ({ ...x, lastFiredAt: e.at }),
        ),
      }
      return mon && e.held === undefined ? countTimedTurn(s1, mon.agentId, e.day) : s1
    }
    case "MonitorStateChanged":
      return {
        ...s,
        monitors: mapWhere(
          s.monitors ?? [],
          (x) => x.id === e.monitorId,
          (x) => ({ ...x, state: e.state, ...(e.ownerId !== undefined ? { ownerId: e.ownerId } : {}) }),
        ),
      }
    case "LooseEndsNoted": {
      // the check woke someone when a key is new since the last check (reactions wake the owners of the new keys)
      const prev = new Set(s.dailyCheck?.keys ?? [])
      const woke = e.keys.some((k) => !prev.has(k))
      return {
        ...s,
        dailyCheck: { lastAt: e.at, keys: e.keys, wokeAt: woke ? e.at : (s.dailyCheck?.wokeAt ?? null) },
      }
    }
    case "SeriesDueNoted": {
      const t = s.items.find((x) => x.id === e.itemId)
      const s1: ProjectState = { ...s, seriesDue: { ...(s.seriesDue ?? {}), [e.itemId]: e.period } }
      return t?.owner?._tag === "agent" && e.held === undefined
        ? countTimedTurn(s1, t.owner.agentId, e.day)
        : s1
    }
    // ---- watchdog, outcomes, privacy ----
    case "RunQuietNoted":
      return updRun(s, e.runId, (r) => ({ ...r, quietAt: e.at }))
    case "StallDetected":
    case "OutcomeCheckScheduled":
    case "OutcomeCheckDue":
      return s
    case "Redacted": {
      const redactAs = redactRecord(e)
      return {
        ...s,
        openAsks: s.openAsks.map(redactAs("comment")),
        memory: s.memory.map(redactAs("memory")),
        artifacts: s.artifacts.map(redactAs("artifact")),
        proposals: s.proposals.map(redactAs("proposal")),
        goals: s.goals.map(redactAs("goal")),
        items: s.items.map(redactAs("item")),
      }
    }
  }
}

type RedactedEvent = Extract<ProjectEvent, { readonly _tag: "Redacted" }>

/**
 * The `Redacted` rule for one record kind: a record whose content was written at `e.seq` (`contentSeq`), still last
 * touched by it (`lastSeq`), or named in `e.refs` gets its `e.fields` strings overwritten with "[redacted]"; others are
 * returned unchanged (the same object).
 */
const redactRecord =
  (e: RedactedEvent) =>
  (tag: EntityRef["_tag"]) =>
  <T extends { readonly id: string; readonly lastSeq: number; readonly contentSeq?: number }>(x: T): T => {
    const named = (e.refs ?? []).some((r) => r._tag === tag && r.id === x.id)
    if (x.contentSeq !== e.seq && x.lastSeq !== e.seq && !named) return x
    const out: Record<string, unknown> = { ...x }
    for (const f of e.fields) if (typeof out[f] === "string") out[f] = "[redacted]"
    return out as T
  }

/**
 * Wave K (§5.2): evolve's `Redacted` rule for one proposal, shared with the Project DO's `decided` projection (a card
 * compacted out of state is invisible to evolve). Unchanged proposals come back as the same object.
 */
export const redactProposal = (p: Proposal, event: RedactedEvent): Proposal =>
  redactRecord(event)("proposal")(p)

/** Fold a sequence of logged events. */
export const fold = (
  s: ProjectState,
  events: ReadonlyArray<readonly [ProjectEvent, EventMeta]>,
): ProjectState => events.reduce((acc, [e, m]) => evolve(acc, e, m), s)
